import type { RiskLimitField, RiskLimits } from '../limits';
import { EMPTY_RISK_LIMITS } from '../limits';
import type { AdvancedRuleFilter } from '../rules';

export type LabRiskSettingsTab =
    | 'firm-defaults'
    | 'symbol-overrides'
    | 'provider-overrides'
    | 'account-overrides'
    | 'advanced-settings';

export interface FirmDefaultsDraftField {
    /** Unchecked rows keep their value but save as "not configured". */
    readonly enabled: boolean;
    /** `null` while the masked number input is empty. */
    readonly value: number | null;
}

export type FirmDefaultsDraft = Readonly<Record<RiskLimitField, FirmDefaultsDraftField>>;

/** Which kind of override rule the shared rule dialog is editing. */
export type RiskRuleScope = 'symbol' | 'provider' | 'account';

export interface RiskRuleDraft {
    /** Provider or account id for scoped rules; `''` for firm-level symbol rules. */
    readonly scopeId: string;
    readonly symbol: string;
    /** Draft limits; `null` means "inherit from firm defaults". */
    readonly maxPrice: number | null;
    readonly maxSize: number | null;
    readonly maxOrderTotal: number | null;
}

export interface RiskRuleDialogState {
    readonly scope: RiskRuleScope;
    readonly mode: 'create' | 'edit';
    /** Server id of the parameter being edited; `null` in create mode. */
    readonly editingId: string | null;
    readonly draft: RiskRuleDraft;
    /** True while the create/update request is in flight. */
    readonly isSaving: boolean;
    /** Message of the last failed save; cleared when a new save starts. */
    readonly error: string | null;
}

export interface LabRiskSettingsState {
    /** Firm scope; `undefined` in superuser "all firms" mode. */
    readonly firmId: string | undefined;
    readonly superUserMode: boolean;
    readonly activeTab: LabRiskSettingsTab;
    /** Last known server firm defaults; the draft's baseline. */
    readonly savedDefaults: RiskLimits;
    readonly draftDefaults: FirmDefaultsDraft;
    /** True while the firm defaults PUT is in flight. */
    readonly isSavingDefaults: boolean;
    readonly advancedFilter: AdvancedRuleFilter;
    readonly advancedSearchInput: string;
    /** Shared add/edit rule dialog; `null` when closed. */
    readonly ruleDialog: RiskRuleDialogState | null;
}

export function draftFromDefaults(defaults: RiskLimits): FirmDefaultsDraft {
    const field = (value: number | null): FirmDefaultsDraftField => ({ enabled: value !== null, value });
    return {
        maxPrice: field(defaults.maxPrice),
        maxSize: field(defaults.maxSize),
        maxOrderTotal: field(defaults.maxOrderTotal),
    };
}

export interface FirmDefaultsDraftParse {
    readonly limits: RiskLimits;
    /** True when an enabled row's value is present but not finite and positive. */
    readonly hasInvalidInput: boolean;
}

export function parseFirmDefaultsDraft(draft: FirmDefaultsDraft): FirmDefaultsDraftParse {
    let hasInvalidInput = false;
    const parseField = (field: FirmDefaultsDraftField): number | null => {
        if (!field.enabled || field.value === null) return null;
        if (!Number.isFinite(field.value) || field.value <= 0) {
            hasInvalidInput = true;
            return null;
        }
        return field.value;
    };
    return {
        limits: {
            maxPrice: parseField(draft.maxPrice),
            maxSize: parseField(draft.maxSize),
            maxOrderTotal: parseField(draft.maxOrderTotal),
        },
        hasInvalidInput,
    };
}

export const EMPTY_RULE_DRAFT: RiskRuleDraft = {
    scopeId: '',
    symbol: '',
    maxPrice: null,
    maxSize: null,
    maxOrderTotal: null,
};

/** The parsed, still scope-less shape of a valid rule draft. */
export interface ParsedRuleDraft extends RiskLimits {
    readonly symbol: string;
}

/**
 * Parse the rule dialog draft; `null` when the symbol is missing or any
 * set limit is not a finite positive number. `null` limits mean
 * "inherit". The symbol is upper-cased.
 */
export function parseRiskRuleDraft(draft: RiskRuleDraft): ParsedRuleDraft | null {
    const symbol = draft.symbol.trim().toUpperCase();
    if (symbol === '') return null;
    const limits = [draft.maxPrice, draft.maxSize, draft.maxOrderTotal];
    if (limits.some((value) => value !== null && (!Number.isFinite(value) || value <= 0))) return null;
    return { symbol, maxPrice: limits[0], maxSize: limits[1], maxOrderTotal: limits[2] };
}

/** Like {@link parseRiskRuleDraft} but also requires a scope (provider/account) selection. */
export function parseScopedRuleDraft(draft: RiskRuleDraft): (ParsedRuleDraft & { scopeId: string }) | null {
    const base = parseRiskRuleDraft(draft);
    if (base === null || draft.scopeId === '') return null;
    return { scopeId: draft.scopeId, ...base };
}

export const initialLabRiskSettingsState: LabRiskSettingsState = {
    firmId: undefined,
    superUserMode: false,
    activeTab: 'firm-defaults',
    savedDefaults: EMPTY_RISK_LIMITS,
    draftDefaults: draftFromDefaults(EMPTY_RISK_LIMITS),
    isSavingDefaults: false,
    advancedFilter: 'all',
    advancedSearchInput: '',
    ruleDialog: null,
};
